/**
 * Aritmética de fechas de calendario sobre strings 'YYYY-MM-DD'.
 *
 * Todo se calcula en UTC a propósito. Un `new Date('2026-08-23')` interpretado en horario
 * local puede caer el 22 según la zona, y el motor de calendario (§4.4) cuenta días
 * hábiles: un día corrido de menos cambia el colchón y por lo tanto una alerta.
 * Como 'YYYY-MM-DD' ordena igual lexicográficamente que cronológicamente, las
 * comparaciones son comparaciones de string.
 */

import type { Fecha } from './types.ts';

const RE_FECHA = /^\d{4}-\d{2}-\d{2}$/;

export function esFechaValida(f: string): f is Fecha {
  if (!RE_FECHA.test(f)) return false;
  const t = aTimestamp(f as Fecha);
  return !Number.isNaN(t) && aFecha(new Date(t)) === f;
}

function assertFecha(f: Fecha): void {
  if (!RE_FECHA.test(f)) throw new TypeError(`Fecha inválida: ${JSON.stringify(f)}`);
}

/** Timestamp UTC de medianoche de esa fecha. */
export function aTimestamp(f: Fecha): number {
  assertFecha(f);
  const y = Number(f.slice(0, 4));
  const m = Number(f.slice(5, 7));
  const d = Number(f.slice(8, 10));
  return Date.UTC(y, m - 1, d);
}

/** Componentes UTC de un Date a 'YYYY-MM-DD'. */
export function aFecha(d: Date): Fecha {
  const y = String(d.getUTCFullYear()).padStart(4, '0');
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * "Hoy" según la zona horaria del usuario, no la del servidor. Un Worker de Cloudflare
 * corre en UTC: sin esto, después de las 21:00 en Chile la app cambiaría de día antes que
 * el usuario.
 */
export function hoyEnZona(zona: string, ahora: Date = new Date()): Fecha {
  try {
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: zona,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    // en-CA con 2-digit produce ya 'YYYY-MM-DD'.
    const s = fmt.format(ahora);
    return RE_FECHA.test(s) ? s : aFecha(ahora);
  } catch {
    return aFecha(ahora);
  }
}

export function addDias(f: Fecha, n: number): Fecha {
  return aFecha(new Date(aTimestamp(f) + n * 86_400_000));
}

/** Días corridos de `a` a `b`. Negativo si `b` es anterior. */
export function diffDias(a: Fecha, b: Fecha): number {
  return Math.round((aTimestamp(b) - aTimestamp(a)) / 86_400_000);
}

/** 0=domingo, 1=lunes … 6=sábado. Coincide con `Date.prototype.getUTCDay`. */
export function diaSemana(f: Fecha): number {
  return new Date(aTimestamp(f)).getUTCDay();
}

/** Fechas de `desde` a `hasta`, ambas inclusive. Vacío si `hasta` < `desde`. */
export function rangoFechas(desde: Fecha, hasta: Fecha): Fecha[] {
  const out: Fecha[] = [];
  const fin = aTimestamp(hasta);
  for (let t = aTimestamp(desde); t <= fin; t += 86_400_000) {
    out.push(aFecha(new Date(t)));
  }
  return out;
}

export const min = (a: Fecha, b: Fecha): Fecha => (a <= b ? a : b);
export const max = (a: Fecha, b: Fecha): Fecha => (a >= b ? a : b);

/** Lunes de la semana de `f`. La semana arranca en lunes (ISO). */
export function inicioSemana(f: Fecha): Fecha {
  const dow = diaSemana(f);
  const retroceso = dow === 0 ? 6 : dow - 1;
  return addDias(f, -retroceso);
}

/** Clave 'YYYY-Www' para agrupar por semana (§5.2). */
export function claveSemana(f: Fecha): string {
  const jueves = addDias(inicioSemana(f), 3); // el jueves define el año ISO
  const anio = Number(jueves.slice(0, 4));
  const primerJueves = (() => {
    for (let d = 1; d <= 7; d++) {
      const cand: Fecha = `${anio}-01-${String(d).padStart(2, '0')}`;
      if (diaSemana(cand) === 4) return cand;
    }
    return `${anio}-01-01` as Fecha;
  })();
  const semana = Math.floor(diffDias(inicioSemana(primerJueves), inicioSemana(f)) / 7) + 1;
  return `${anio}-W${String(semana).padStart(2, '0')}`;
}

/**
 * Ventana móvil que TERMINA en `hasta` y abarca `dias` días, ambos extremos inclusive.
 * Una ventana de 7 días que termina el 14 arranca el 8, no el 7.
 */
export function ventana(hasta: Fecha, dias: number): { desde: Fecha; hasta: Fecha } {
  return { desde: addDias(hasta, -(dias - 1)), hasta };
}

export function enVentana(f: Fecha, v: { desde: Fecha; hasta: Fecha }): boolean {
  return f >= v.desde && f <= v.hasta;
}
